import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ColorNames, Utils } from 'marketpro-design-lib';
import {
  Color,
  ColorShades,
  debounceTimer,
} from 'projects/marketpro-design-lib/src';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, tap } from 'rxjs/operators';
import { constants } from '../../../constants';
import { AuthService } from '../../auth/services/auth.service';
import { AgeGroup } from '../models/age-group.model';
import { RangeFilter } from '../models/custom-filter-range.model';
import { RangeFilterType } from '../models/range-filter-type.enum';
import { SpendingGroup } from '../models/spending-group.model';
import { IncomeGroup } from '../models/income-group.model';
import { Permissions } from '../../auth/models/permissions.model';

export class SystemGroups {
  ageGroups: AgeGroup[] = [];
  spendingGroups: SpendingGroup[] = [];
  incomeGroups: IncomeGroup[] = [];
  generationGroups: string[] = [];
}

@Injectable({
  providedIn: 'root',
})
export class SystemGroupsService implements OnDestroy {
  private _systemGroups: SystemGroups = new SystemGroups();
  private _groupedSpendingGroups?: SpendingGroup[] = undefined;
  private ngUnsubscribe: Subject<void> = new Subject();
  private permissions: Permissions;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.authService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(debounceTimer))
      .subscribe(() => {
        this._groupedSpendingGroups = undefined;
      });
    this.authService.permissions$.subscribe(
      (permissions) => (this.permissions = permissions)
    );
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public get systemGroups(): SystemGroups {
    return this._systemGroups;
  }

  public groupedSpendingGroups(
    spendingGroups: SpendingGroup[]
  ): SpendingGroup[] {
    if (this._groupedSpendingGroups) {
      return this._groupedSpendingGroups;
    }

    const poorGroup = new SpendingGroup('', undefined, 13, undefined);
    const consumerGroup = new SpendingGroup(
      '',
      13,
      undefined,
      undefined,
      [],
      undefined,
      true
    );
    const allSpendingGroup = new SpendingGroup();
    constants.data.systemSpendingGroups
      .sort((a, b) => {
        if (a.greaterThan === b.greaterThan) {
          return -this.compare(a.lessThan, b.lessThan);
        }

        return -this.compare(a.greaterThan, b.greaterThan);
      })
      .forEach((spendingGroup) => {
        if (
          spendingGroup.greaterThan === poorGroup.greaterThan &&
          spendingGroup.lessThan === poorGroup.lessThan
        ) {
          poorGroup.name = spendingGroup.name;
          poorGroup.id = spendingGroup.id;
          return;
        }
        if (
          spendingGroup.greaterThan === consumerGroup.greaterThan &&
          spendingGroup.lessThan === consumerGroup.lessThan
        ) {
          consumerGroup.name = spendingGroup.name;
          consumerGroup.id = spendingGroup.id;
          return;
        }

        if (
          spendingGroup.lessThan !== undefined &&
          spendingGroup.lessThan <= 13
        ) {
          // Poor
          poorGroup.subgroups.push(spendingGroup);
        } else if (spendingGroup.greaterThan || spendingGroup.lessThan) {
          // Consumer
          consumerGroup.subgroups.push(spendingGroup);
        }
      });

    this._groupedSpendingGroups = [allSpendingGroup, consumerGroup, poorGroup];

    return this._groupedSpendingGroups;
  }

  public getSystemGroupsRequest(): Observable<SystemGroups> {
    let systemGroupsObservable: Observable<SystemGroups>;
    let generationsObservable: Observable<string[]>;
    if (!this.authService.isLoggedIn()) {
      systemGroupsObservable = of({
        ageGroups: constants.data.systemAgeGroups,
        spendingGroups: constants.data.systemSpendingGroups,
        incomeGroups: constants.data.systemIncomeGroups,
        generationGroups: [],
      });
      generationsObservable = of([]);
    } else {
      generationsObservable = this.http.get<string[]>(
        constants.uriParts.getGenerations
      );
      systemGroupsObservable = this.http
        .get<RangeFilter[]>(constants.uriParts.defaultRangeFilters)
        .pipe(
          map((rangeFilters: RangeFilter[]) => {
            const systemGroups = rangeFilters.reduce((acc, curr) => {
              if (curr.type === RangeFilterType.AGE) {
                acc.ageGroups.push(AgeGroup.from(curr));
              } else if (curr.type === RangeFilterType.SPENDING) {
                acc.spendingGroups.push(SpendingGroup.from(curr));
              } else if (curr.type === RangeFilterType.INCOME) {
                acc.incomeGroups.push(
                  IncomeGroup.from({ ...curr, name: 'Income' })
                );
              }
              return acc;
            }, new SystemGroups());
            systemGroups.incomeGroups.sort(
              (a, b) => (b.greaterThan || 0) - (a.greaterThan || 0)
            );
            !this.permissions.isGenerationsAvailable &&
              systemGroups.ageGroups.unshift(new AgeGroup());
            systemGroups.spendingGroups.unshift(new SpendingGroup());
            systemGroups.incomeGroups.unshift(new IncomeGroup());
            return systemGroups;
          })
        );
    }
    return !this.permissions.isGenerationsAvailable
      ? systemGroupsObservable.pipe(
          tap((systemGroups) => (this._systemGroups = systemGroups))
        )
      : combineLatest([systemGroupsObservable, generationsObservable]).pipe(
          map(
            ([systemGroups, generations]) =>
              (this._systemGroups = {
                ...systemGroups,
                generationGroups: this.permissions.isFreemium
                  ? ['All generations', ...generations]
                  : generations,
              })
          )
        );
  }

  public getSpendingGroupColor(
    gte?: number,
    lte?: number,
    shade: number = 600
  ): Color {
    if (!gte && !lte) {
      return { name: ColorNames.Blue, shade: ColorShades.Shade0 };
    }

    if (gte && gte >= 130) {
      return { name: ColorNames.Green, shade };
    }

    if (lte && lte <= 13) {
      return { name: ColorNames.Red, shade };
    }

    if (gte && gte < 130 && lte && lte >= 13) {
      return { name: ColorNames.Apricot, shade };
    }

    return { name: ColorNames.Blue, shade };
  }

  public getSpendingGroupColorHex(
    gte?: number,
    lte?: number,
    shade: number = 600
  ): string {
    return Utils.getColorHex(this.getSpendingGroupColor(gte, lte, shade));
  }

  // Copied from java comparators, handling null values and numbers
  private compare(a?: number, b?: number): number {
    if (a === undefined) {
      return b === undefined ? 0 : -1;
    } else if (b === undefined) {
      return 1;
    }

    return a < b ? -1 : a === b ? 0 : 1;
  }
}
